import { chronicleIsRecordingAtom } from '@/atoms/chronicle/session';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';
import { useAtomValue } from 'jotai';

/**
 * Whether a clinical recording is active, **including paused recordings**.
 *
 * A running timer alone is not enough for "don't interrupt a clinical
 * session" gates (modal priority stack R1): pausing stops the timer while the
 * session is still mid-consult. This combines
 * - the caller-provided running-timer signal (`timerModel.isRunning` — passed
 *   in rather than read here, per the Rematch containment ratchet),
 * - the open session's server-tracked `recording_status` (RECORDING or
 *   PAUSED — mirrors `useSessionStage`'s "during" recording check), and
 * - an active Chronicle device recording (`chronicleIsRecordingAtom` stays
 *   true while paused — the device session ID persists until stop).
 *
 * Off a session route `useCurrentSessionId()` is empty, so the query is
 * disabled and only the timer / Chronicle signals apply.
 */
export function useIsClinicalRecordingActive(isTimerRunning: boolean): boolean {
  const isChronicleRecording = useAtomValue(chronicleIsRecordingAtom);
  const sessionId = useCurrentSessionId();
  const { data: recordingStatus } = useQuerySessionById({
    sessionId,
    select: (data) => data?.recording_status,
  });

  return (
    isTimerRunning ||
    isChronicleRecording ||
    recordingStatus === RecordingStatus.RECORDING ||
    recordingStatus === RecordingStatus.PAUSED
  );
}
